import React, { useCallback, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Swal from 'sweetalert2';
import { useLocationState } from '../../../../Common/Util/CustomHooks';
import * as request from '../../../../Common/Util/Request';
import { getSaleStatusText } from '../../../../Common/Util/LectureSaleStatus';
import * as Button from '../../../../Common/Component/Button';
import * as Model from '../../../../Common/Model';
import Table from '../../../../Common/Component/Table';
import './index.scss';
import moment from 'moment';
import * as excel from '../../../../Common/Util/Excel';
import DatePicker from '../../../../Common/Component/DatePicker';
import { useHistory } from 'react-router-dom';
import { EJE_THEME, LECTURE } from '../../../../Common/Constant';
import ManageWaitings from '../../Single/ManageWaitings';
import withReactContent from 'sweetalert2-react-content';
import { buildThirdPartyExcelRow } from '../../../../Common/Util/ThirdPartyExcelRow';
import { Input, MenuItem, Select, ThemeProvider } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import SchedulePickerModal from '../../components/SchedulePickerModal';

function ThirdPartyList(props) {
  const intl = useIntl();
  const history = useHistory();
  const SwalWithReactContent = withReactContent(Swal);

  const [locationState, setLocationState] = useLocationState({
    searchType: 'title',
    searchText: '',
    sortType: 'latest',
    filters: [],
  });
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);
  const [searchType, setSearchType] = useState(locationState.searchType);
  const [searchText, setSearchText] = useState(locationState.searchText);
  const [sortType, setSortType] = useState(locationState.sortType || 'latest');

  const onClickSearch = useCallback(() => {
    setLocationState({
      searchType,
      searchText,
      sortType,
      filters: searchText ? [{ id: searchType, value: searchText }] : [],
    });
  }, [setLocationState, searchType, searchText, sortType]);

  const onChangeSortType = useCallback(
    (e) => {
      const nextSortType = e.target.value;
      setSortType(nextSortType);
      setLocationState({ sortType: nextSortType });
    },
    [setLocationState],
  );

  const [lectures, setLectures] = useState([]);
  const [total, setTotal] = useState(0);

  const fetchData = useCallback(
    ({ pageIndex, pageSize }) => {
      const params = {
        page: pageIndex + 1,
        limit: pageSize,
        filtered: JSON.stringify(locationState.filters),
        sorted: JSON.stringify([
          {
            id: locationState.sortType === 'latest' ? 'updatedAt' : 'id',
            desc: true,
          },
        ]),
        host: 'gov',
      };
      request.lectures('single', params).then(({ classes }) => {
        setLectures(classes.rows.map((row) => new Model.Lecture(row)));
        setTotal(classes.count);
      });
    },
    [locationState.filters, locationState.sortType],
  );

  const loadUsers = useCallback(
    ({ page, limit, lectureId }) => {
      const params = {
        page,
        limit,
        all_people: true,
        filtered: JSON.stringify([{ id: 'lectureId', value: lectureId }]),
      };
      if (startDate) {
        params.start_date = moment(startDate).format('YYYY-MM-DD');
      }
      if (endDate) {
        params.end_date = moment(endDate).format('YYYY-MM-DD');
      }

      return request.lectureUsers(params).then(({ userClasses }) => userClasses);
    },
    [startDate, endDate],
  );

  const performDownload = useCallback(
    (lecture, selectedScheduleIds) => {
      setDownloadingId(lecture.id);
      loadUsers({ page: 1, limit: 9999999, all_people: true, lectureId: lecture.id }).then((users) => {
        const filteredRows =
          selectedScheduleIds && selectedScheduleIds.size > 0
            ? users.rows.filter(
                (user) =>
                  selectedScheduleIds.has(user.FK_class_offline_schedule_id) ||
                  selectedScheduleIds.has(user.FK_class_online_schedule_id),
              )
            : users.rows;

        if (filteredRows.length > 0) {
          const data = filteredRows.map((user) => buildThirdPartyExcelRow(user, intl));
          excel.downloadXLSX(data, `수강회원관리(${lecture.title})_${moment.utc().format('YYYYMMDD')}`);
          setDownloadingId(false);
        } else {
          Swal.fire({
            html: `<div style="margin-top:20px; font-weight: bold;">설정한 기간 내에 결제한 수강자가 없습니다.</div>`,
            toast: true,
            position: 'center',
            timer: 2000,
            timerProgressBar: true,
            showConfirmButton: true,
            icon: 'error',
            type: 'error',
          });
          setDownloadingId(false);
        }
      });
    },
    [intl, loadUsers],
  );

  const onClickCSV = useCallback(
    (lecture) => {
      if (downloadingId) return;
      if (!lecture.userClassCount && !lecture.userClassReadyCount) {
        Swal.fire({
          html: `<div style="margin-top:20px; font-weight: bold;">해당 강의의 수강자가 없습니다.</div>`,
          toast: true,
          position: 'center',
          timer: 2000,
          timerProgressBar: true,
          showConfirmButton: true,
          icon: 'error',
          type: 'error',
        });
        return;
      }
      if (LECTURE.ROUND_LISTEN_TYPES.includes(lecture.listen_type)) {
        SwalWithReactContent.fire({
          title: `기수 선택 - ${lecture.title}`,
          html: (
            <SchedulePickerModal
              lecture={lecture}
              onConfirm={(selectedIds) => {
                Swal.close();
                performDownload(lecture, selectedIds);
              }}
            />
          ),
          showConfirmButton: false,
          showCloseButton: true,
          width: '600px',
        });
      } else {
        performDownload(lecture, null);
      }
    },
    [SwalWithReactContent, downloadingId, performDownload],
  );

  const showManageWaitings = (lecture) => {
    SwalWithReactContent.fire({
      title: `[기수 오픈 안내] - ${lecture.title}`,
      html: <ManageWaitings data={lecture} />,
      showCloseButton: true,
      allowOutsideClick: false,
      showConfirmButton: false,
      width: '1200px',
    });
  };

  const showStudentInfo = (lecture) => {
    const {
      title,
      userClassCount,
      userClassReadyCount,
      listen_type,
      ClassOfflineSchedules,
      ClassConsultingSchedules,
      ClassOnlineSchedule,
      ClassOnlineScheduleByRounds,
    } = lecture;
    if (LECTURE.ROUND_LISTEN_TYPES.includes(listen_type)) {
      let schedules = '';
      if (listen_type === 'offline') {
        ClassOfflineSchedules.map((lecture) => {
          const {
            class_round,
            title,
            start_date,
            end_date,
            class_start_time,
            student_limit,
            student_count,
            student_ready_count,
            class_dates,
          } = lecture;
          schedules += `<li>
        ${class_round}기, 
        ${title ? `${title}` : `옵션명 없음`}, 
        ${class_dates
          .split('')
          .map((weekday, index) => {
            return +weekday !== 0 ? intl.formatMessage({ id: 'ID_WEEKDAY_' + (index + 1) }) : '';
          })
          .join('')}, 
        ${class_start_time}, 
        ${start_date}~${end_date} : 
        ${`${(student_count || 0) + (student_ready_count || 0)}명`} / ${
            student_limit === 0 ? '제한없음' : `${student_limit}명`
          }
        </li>`;
          return lecture;
        });
      } else if (listen_type === 'consulting') {
        ClassConsultingSchedules.forEach((lecture) => {
          const {
            class_round,
            title,
            start_date,
            end_date,
            class_start_time,
            student_limit,
            student_count,
            student_ready_count,
            class_dates,
          } = lecture;
          schedules += `<li>
        ${class_round}기, 
        ${title ? `${title}` : `옵션명 없음`}, 
        ${class_dates
          .split('')
          .map((weekday, index) => {
            return +weekday !== 0 ? intl.formatMessage({ id: 'ID_WEEKDAY_' + (index + 1) }) : '';
          })
          .join('')}, 
        ${class_start_time}, 
        ${start_date}~${end_date} : 
        ${`${(student_count || 0) + (student_ready_count || 0)}명`} / ${
            student_limit === 0 ? '제한없음' : `${student_limit}명`
          }
        </li>`;
        });
      } else {
        ClassOnlineScheduleByRounds.forEach((lecture) => {
          const {
            start_date,
            end_date,
            class_start_time,
            student_limit,
            student_count,
            student_ready_count,
            class_round,
            title,
          } = lecture;
          schedules += `<li>
        ${class_round ? `${class_round}기` : '기수 없음'},
        ${title ? `${title}` : `옵션명 없음`}, 
        ${class_start_time ? class_start_time : '시작 시간 없음'}, 
        ${start_date ? start_date : '시작일 없음'}~${end_date ? end_date : '종료일 없음'} : 
        ${(student_count || 0) + (student_ready_count || 0)}명 / ${
            student_limit === 0 ? '제한없음' : `${student_limit}명`
          }
        </li>`;
        });
      }
      Swal.fire({
        title,
        html: `
        <ol>
          <li>총 수강자
            <ul>
              <li>
                ${(userClassCount || 0) + (userClassReadyCount || 0)}명
                ${userClassReadyCount ? `(결제대기 : ${userClassReadyCount}명)` : ''}
              </li>
            </ul>
          </li>
          <li>수강 옵션별 수강자
            <ul>
              ${schedules}
            </ul>
          </li>
        </ol>
        `,
        showCloseButton: true,
        showConfirmButton: false,
      });
    } else {
      const { student_limit } = ClassOnlineSchedule || { student_limit: 0 };
      return Swal.fire({
        title,
        html: `
        <ol>
          <li>총 수강자
            <ul>
              <li>
                ${(userClassCount || 0) + (userClassReadyCount || 0)}명
                ${+student_limit !== 0 ? ` / ${student_limit}명` : ``}
                ${userClassReadyCount ? `(결제대기 : ${userClassReadyCount}명)` : ''}
              </li>
            </ul>
          </li>
        </ol>
        `,
        showCloseButton: true,
        showConfirmButton: false,
      });
    }
  };

  return (
    <ThemeProvider theme={EJE_THEME}>
      <div className="third-party-list">
        <section className="third-party-list__header">
          <div className="third-party-list__search">
            <Select
              value={searchType}
              onChange={(e) => setSearchType(e.target.value)}
              displayEmpty
              color="pink"
              size="small"
            >
              <MenuItem value="title" sx={{ fontSize: '0.875rem' }}>
                강의명
              </MenuItem>
              <MenuItem value="name" sx={{ fontSize: '0.875rem' }}>
                강사명
              </MenuItem>
            </Select>

            <Input
              value={searchText}
              placeholder="검색어를 입력하세요."
              color="pink"
              onChange={(e) => setSearchText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  onClickSearch();
                }
              }}
            />

            <Button.MUI
              onClick={onClickSearch}
              variant="outlined"
              endIcon={<SearchIcon fontSize="small" />}
              color="pink"
              sx={{
                height: '2rem',
                minHeight: '2rem',
                minWidth: '74px',
                px: '12px',
                lineHeight: 1.2,
                whiteSpace: 'nowrap',
              }}
            >
              검색
            </Button.MUI>
          </div>
          <div className="third-party-list__actions">
            <div className="making-btn">
              <Button.Positive onClick={() => window.open(`/lecture/single/new`)}>신규 강의 생성(단일)</Button.Positive>
            </div>
          </div>
        </section>
        <div className="third-party-list-top-container">
          <div className="third-party-list-top-left">
            <div className="third-party-list__total">총 강의수 {total}</div>
          </div>
        </div>
        <div className="third-party-list-table-top">
          <div className="third-party-list__sort">
            <Select value={sortType} onChange={onChangeSortType} size="small" color="pink" displayEmpty>
              <MenuItem value="latest" sx={{ fontSize: '0.875rem' }}>
                최신순
              </MenuItem>
              <MenuItem value="id_desc" sx={{ fontSize: '0.875rem' }}>
                강의ID 역순
              </MenuItem>
            </Select>
          </div>
          <div className="third-party-list__date">
            *수강자 다운 기간 : &nbsp;
            <DatePicker selected={startDate} maxDate={endDate} onChange={(date) => setStartDate(date)} />
            ~
            <DatePicker selected={endDate} minDate={startDate} onChange={(date) => setEndDate(date)} />
          </div>
        </div>
        <Table
          data={lectures}
          total={total}
          pageSize={20}
          columns={[
            { Header: '강의ID', accessor: 'id' },
            { Header: '강의명', accessor: 'title' },
            {
              Header: '강사명',
              accessor: 'Class_Tutors',
              Cell: ({ value: Class_Tutors }) =>
                Class_Tutors.map((Class_Tutor) => Class_Tutor.FK_tutor.name).join(', ') || '-',
            },
            {
              Header: '판매가능',
              accessor: 'is_selling',
              Cell: ({ row }) => getSaleStatusText(row.original),
            },
            {
              Header: '커리큘럼',
              accessor: 'ClassChapters',
              Cell: ({ value: ClassChapters }) => {
                const allChaptersLength =
                  ClassChapters.map((ClassChapter) => ClassChapter.ClassSections.length).reduce(
                    (acc, cur) => acc + cur,
                    0,
                  ) || '-';

                const needUpdateChaptersLength =
                  ClassChapters.map(
                    (ClassChapter) =>
                      ClassChapter.ClassSections.map((ClassSection) => (!ClassSection.FK_video_id ? 1 : 0)).reduce(
                        (acc, cur) => acc + cur,
                        0,
                      ) || 0,
                  ).reduce((acc, cur) => acc + cur, 0) || 0;
                return (
                  <>
                    {allChaptersLength}
                    {needUpdateChaptersLength ? (
                      <span
                        style={{
                          color: 'gray',
                          fontSize: '0.9rem',
                          marginLeft: 5,
                        }}
                      >
                        ({needUpdateChaptersLength})
                      </span>
                    ) : (
                      ''
                    )}
                  </>
                );
              },
            },
            {
              Header: '구분',
              accessor: 'listen_type',
              Cell: ({ value: listen_type }) => <FormattedMessage id={'ID_LISTEN_TYPE_' + listen_type.toUpperCase()} />,
            },
            {
              Header: '수강자',
              accessor: (row) => (
                <span className="user-class-count" onClick={() => showStudentInfo(row)}>
                  {(row.userClassCount || 0) + (row.userClassReadyCount || 0)}명
                </span>
              ),
            },
            {
              Header: '수강자 관리',
              accessor: (row) => (
                <button
                  className="user-class-count btn-detail"
                  onClick={() => history.push(`/lecture/3rd/users/${row.id}`)}
                >
                  관리
                </button>
              ),
            },
            {
              Header: '대기 명단',
              accessor: (row) => {
                if (!LECTURE.ROUND_LISTEN_TYPES.includes(row.listen_type)) {
                  return '-';
                }
                return (
                  <span className="waiting-count" onClick={() => showManageWaitings(row)}>
                    {row.waitingUsers?.length || 0}명
                  </span>
                );
              },
            },
            {
              Header: '수강자 다운',
              accessor: (row) => (
                <button className="btn-detail btn-download" onClick={() => !downloadingId && onClickCSV(row)}>
                  {row.id === downloadingId ? 'loading...' : '액셀 다운'}
                </button>
              ),
            },
            {
              Header: '평점',
              accessor: (row) => <span className="user-class-rate">{row.rate || 0}</span>,
            },
            {
              Header: '정보수정',
              accessor: (row) => (
                <button className="btn-detail" onClick={() => window.open(`/lecture/single/new/${row.id}`)}>
                  수정
                </button>
              ),
            },
          ]}
          fetchData={fetchData}
        />
      </div>
    </ThemeProvider>
  );
}

export default ThirdPartyList;
